import { create } from "zustand";

import type { JsonObject } from "@/lib/api/types/json";
import { extractChannelId } from "@/lib/ws/routing";
import {
  createMockRealtimeClient,
  isMockRealtimeUrl,
  type MockRealtimeClient,
} from "@/lib/mock/websocket/mock-socket";
import {
  createRealtimeClient,
  type RealtimeClient,
  type RealtimeClientOptions,
} from "@/lib/ws/client";
import type { RealtimeConnectionState } from "@/lib/ws/types";

export const MAX_ALERT_STREAM = 10_000;

export type DevicePresence = "online" | "offline";

export type AlertStreamEntry = {
  id: string;
  createdAt: number;
  payload: JsonObject;
};

/** Latest command lifecycle patch from WebSocket (merged per command id). */
export type CommandLivePatch = {
  id: string;
  device_id: string | null;
  status: string | null;
  response: JsonObject | null;
  updatedAt: number;
};

type RealtimeStoreState = {
  connectionStatus: RealtimeConnectionState;
  /**
   * Latest telemetry per device. The same Map instance is mutated in place for throughput.
   * Subscribe to `telemetryRevision` (or narrow selectors) to re-render when data changes.
   */
  liveTelemetry: Map<string, JsonObject>;
  telemetryRevision: number;
  deviceStatus: Map<string, DevicePresence>;
  deviceStatusRevision: number;
  alertsStream: AlertStreamEntry[];
  alertsRevision: number;
  commandsById: Map<string, CommandLivePatch>;
  commandsRevision: number;
  setConnectionStatus: (s: RealtimeConnectionState) => void;
  resetRealtimeData: () => void;
};

function makeAlertId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function coerceRecord(msg: unknown): Record<string, unknown> | null {
  if (!msg || typeof msg !== "object" || Array.isArray(msg)) {
    return null;
  }
  return msg as Record<string, unknown>;
}

function readDeviceId(o: Record<string, unknown>): string | undefined {
  const a = o.device_id;
  const b = o.deviceId;
  if (typeof a === "string") {
    return a;
  }
  if (typeof b === "string") {
    return b;
  }
  return undefined;
}

function asJsonObject(raw: unknown): JsonObject {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as JsonObject;
  }
  return {};
}

const telemetryBatch = new Map<string, JsonObject>();
let telemetryFlushScheduled = false;

function scheduleTelemetryFlush(): void {
  if (telemetryFlushScheduled) {
    return;
  }
  telemetryFlushScheduled = true;
  queueMicrotask(() => {
    telemetryFlushScheduled = false;
    if (telemetryBatch.size === 0) {
      return;
    }
    const entries = Array.from(telemetryBatch.entries());
    telemetryBatch.clear();
    useRealtimeStore.setState((s) => {
      for (const [deviceId, data] of entries) {
        s.liveTelemetry.set(deviceId, data);
      }
      return { telemetryRevision: s.telemetryRevision + 1 };
    });
  });
}

function queueTelemetry(deviceId: string, data: JsonObject): void {
  telemetryBatch.set(deviceId, data);
  scheduleTelemetryFlush();
}

const deviceBatch = new Map<string, DevicePresence>();
let deviceFlushScheduled = false;

function scheduleDeviceFlush(): void {
  if (deviceFlushScheduled) {
    return;
  }
  deviceFlushScheduled = true;
  queueMicrotask(() => {
    deviceFlushScheduled = false;
    if (deviceBatch.size === 0) {
      return;
    }
    const entries = Array.from(deviceBatch.entries());
    deviceBatch.clear();
    useRealtimeStore.setState((s) => {
      for (const [deviceId, st] of entries) {
        s.deviceStatus.set(deviceId, st);
      }
      return { deviceStatusRevision: s.deviceStatusRevision + 1 };
    });
  });
}

function queueDeviceStatus(deviceId: string, status: DevicePresence): void {
  deviceBatch.set(deviceId, status);
  scheduleDeviceFlush();
}

const alertQueue: AlertStreamEntry[] = [];
let alertFlushScheduled = false;

function scheduleAlertFlush(): void {
  if (alertFlushScheduled) {
    return;
  }
  alertFlushScheduled = true;
  queueMicrotask(() => {
    alertFlushScheduled = false;
    if (alertQueue.length === 0) {
      return;
    }
    const batch = alertQueue.splice(0, alertQueue.length);
    useRealtimeStore.setState((s) => {
      const merged = s.alertsStream.concat(batch);
      const trimmed =
        merged.length > MAX_ALERT_STREAM ? merged.slice(-MAX_ALERT_STREAM) : merged;
      return {
        alertsStream: trimmed,
        alertsRevision: s.alertsRevision + 1,
      };
    });
  });
}

function queueAlert(entry: AlertStreamEntry): void {
  alertQueue.push(entry);
  scheduleAlertFlush();
}

function ingestTelemetryMessage(msg: unknown): void {
  const o = coerceRecord(msg);
  if (!o) {
    return;
  }
  const deviceId = readDeviceId(o);
  if (!deviceId) {
    return;
  }
  const rawPayload = o.data ?? o.payload;
  const data = rawPayload !== undefined ? asJsonObject(rawPayload) : {};
  queueTelemetry(deviceId, data);
}

function ingestDeviceStatusMessage(msg: unknown): void {
  const o = coerceRecord(msg);
  if (!o) {
    return;
  }
  const deviceId = readDeviceId(o);
  const status = o.status;
  if (!deviceId || (status !== "online" && status !== "offline")) {
    return;
  }
  queueDeviceStatus(deviceId, status);
}

function ingestAlertMessage(msg: unknown): void {
  const o = coerceRecord(msg);
  if (!o) {
    return;
  }
  const id = typeof o.id === "string" ? o.id : makeAlertId();
  const createdRaw = o.created_at ?? o.createdAt ?? o.ts;
  const createdAt =
    typeof createdRaw === "number"
      ? createdRaw
      : typeof createdRaw === "string"
        ? Date.parse(createdRaw) || Date.now()
        : Date.now();
  queueAlert({
    id,
    createdAt,
    payload: asJsonObject(o.payload ?? o),
  });
}

type QueuedCommandDelta = {
  id: string;
  device_id?: string | null;
  status?: string | null;
  response?: JsonObject | null;
};

const commandDeltaBatch = new Map<string, QueuedCommandDelta>();
let commandBatchFlushScheduled = false;

function mergeQueuedCommandDelta(a: QueuedCommandDelta, b: QueuedCommandDelta): QueuedCommandDelta {
  return {
    id: b.id,
    device_id: b.device_id !== undefined ? b.device_id : a.device_id,
    status: b.status !== undefined ? b.status : a.status,
    response: b.response !== undefined ? b.response : a.response,
  };
}

function scheduleCommandBatchFlush(): void {
  if (commandBatchFlushScheduled) {
    return;
  }
  commandBatchFlushScheduled = true;
  queueMicrotask(() => {
    commandBatchFlushScheduled = false;
    if (commandDeltaBatch.size === 0) {
      return;
    }
    const batch = Array.from(commandDeltaBatch.entries());
    commandDeltaBatch.clear();
    useRealtimeStore.setState((s) => {
      const next = new Map(s.commandsById);
      for (const [id, delta] of batch) {
        const prev = next.get(id);
        let nextResponse = prev?.response ?? null;
        if (delta.response !== undefined) {
          nextResponse = delta.response;
        }
        next.set(id, {
          id: delta.id,
          device_id: delta.device_id !== undefined ? delta.device_id : (prev?.device_id ?? null),
          status: delta.status !== undefined ? delta.status : (prev?.status ?? null),
          response: nextResponse,
          updatedAt: Date.now(),
        });
      }
      return { commandsById: next, commandsRevision: s.commandsRevision + 1 };
    });
  });
}

function ingestCommandMessage(msg: unknown): void {
  const channel = extractChannelId(msg);
  if (channel !== "command" && channel !== "commands") {
    return;
  }
  const o = coerceRecord(msg);
  if (!o) {
    return;
  }
  const inner = o.payload !== undefined && o.payload !== null ? coerceRecord(o.payload) : null;
  const src = inner ?? o;
  const idRaw = src.command_id ?? src.commandId ?? src.id ?? o.command_id ?? o.commandId ?? o.id;
  if (typeof idRaw !== "string") {
    return;
  }
  const st = src.status ?? o.status;
  const rawResp = src.response !== undefined ? src.response : o.response;
  const deviceId = readDeviceId(src) ?? readDeviceId(o);

  const delta: QueuedCommandDelta = { id: idRaw };
  if (deviceId !== undefined) {
    delta.device_id = deviceId;
  }
  if (typeof st === "string") {
    delta.status = st;
  }
  if (rawResp !== undefined) {
    delta.response = rawResp === null ? null : asJsonObject(rawResp);
  }

  const existing = commandDeltaBatch.get(idRaw);
  commandDeltaBatch.set(idRaw, existing ? mergeQueuedCommandDelta(existing, delta) : delta);
  scheduleCommandBatchFlush();
}

export const useRealtimeStore = create<RealtimeStoreState>()((set) => ({
  connectionStatus: "disconnected",
  liveTelemetry: new Map<string, JsonObject>(),
  telemetryRevision: 0,
  deviceStatus: new Map<string, DevicePresence>(),
  deviceStatusRevision: 0,
  alertsStream: [],
  alertsRevision: 0,
  commandsById: new Map<string, CommandLivePatch>(),
  commandsRevision: 0,

  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),

  resetRealtimeData: () => {
    telemetryBatch.clear();
    deviceBatch.clear();
    alertQueue.length = 0;
    commandDeltaBatch.clear();
    set({
      liveTelemetry: new Map(),
      telemetryRevision: 0,
      deviceStatus: new Map(),
      deviceStatusRevision: 0,
      alertsStream: [],
      alertsRevision: 0,
      commandsById: new Map(),
      commandsRevision: 0,
    });
  },
}));

export const REALTIME_CHANNELS = {
  telemetry: "telemetry",
  deviceStatus: "device_status",
  alert: "alert",
  alerts: "alerts",
  command: "command",
  commands: "commands",
} as const;

export function wireRealtimeClient(client: RealtimeClient): () => void {
  const unsubs: (() => void)[] = [];

  unsubs.push(
    client.onConnectionState((connectionStatus) => {
      useRealtimeStore.getState().setConnectionStatus(connectionStatus);
    }),
  );

  unsubs.push(client.subscribe(REALTIME_CHANNELS.telemetry, ingestTelemetryMessage));
  unsubs.push(client.subscribe(REALTIME_CHANNELS.deviceStatus, ingestDeviceStatusMessage));
  unsubs.push(client.subscribe(REALTIME_CHANNELS.alert, ingestAlertMessage));
  unsubs.push(client.subscribe(REALTIME_CHANNELS.alerts, ingestAlertMessage));
  unsubs.push(client.subscribe(REALTIME_CHANNELS.command, ingestCommandMessage));
  unsubs.push(client.subscribe(REALTIME_CHANNELS.commands, ingestCommandMessage));

  return () => {
    for (const u of unsubs) {
      u();
    }
  };
}

type ActiveRealtimeClient = RealtimeClient | MockRealtimeClient;

let activeClient: ActiveRealtimeClient | null = null;
let activeUnwire: (() => void) | null = null;

export function connectRealtime(
  url: string,
  options?: RealtimeClientOptions,
): ActiveRealtimeClient {
  disconnectRealtime();
  const client = isMockRealtimeUrl(url) ? createMockRealtimeClient() : createRealtimeClient(url, options);
  activeClient = client;
  activeUnwire = wireRealtimeClient(client as unknown as RealtimeClient);
  client.connect();
  return client;
}

export function disconnectRealtime(): void {
  activeUnwire?.();
  activeUnwire = null;
  activeClient?.disconnect();
  activeClient = null;
  useRealtimeStore.getState().setConnectionStatus("disconnected");
}

export function getActiveRealtimeClient(): ActiveRealtimeClient | null {
  return activeClient;
}
